import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, tap, map } from "rxjs";
import { environment } from "src/environments/environment";
import { jwtDecode } from "jwt-decode";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import {
  NavigationItem,
  NavigationItems,
} from "src/app/layout/admin/navigation/navigation";
import {
  getSidebarAccessRule,
  toPermissionConfig,
} from "../config/sidebar-access";
import {
  AuthLoginRequestDto,
  AuthMeResponseDto,
  AuthResponseDto,
  BackendJwtClaims,
} from "../models/auth-api.dto";
import { AlertService } from "./alert.service";

export interface Permiso {
  modulo: string;
  accion: string;
  entidad: string;
}

export interface CurrentUser {
  id: number;
  usuarioNombre: string;
  rolNombre: string;
  roles?: string[];
  permisos: Permiso[];
}

export interface LoginResponse {
  token: string;
  usuario?: string;
  rol: string;
  roles?: string[];
  usuarioId: number;
  permisos: Permiso[];
}

export interface DecodedToken {
  uid?: number;
  usuarioId?: number;
  roles?: string[];
  permisos?: string[];
  rol?: string;
  sub?: string;
  exp?: number;
  iat?: number;
}

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private readonly backendApiUrl: string = this.normalizeBaseUrl(
    environment.apiEndpoint,
  );

  /**
   * backendAuthUrl:
   * - Backend real: /api/auth
   */
  private readonly backendAuthUrl: string = `${this.backendApiUrl}auth`;

  private tokenKey = "jwt_token";
  private userProfileKey = "user_profile";
  private readonly PERMISOS_KEY = "auth_permisos";

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private currentUserSubject = new BehaviorSubject<CurrentUser | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  private permisosSubject = new BehaviorSubject<Permiso[]>([]);
  public permisos$ = this.permisosSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private ngbModal: NgbModal,
    private alertService: AlertService,
  ) {
    this.initializeAuth();
  }

  private normalizeBaseUrl(url: string): string {
    const value = String(url || "").trim();
    if (!value) return "/";
    return value.endsWith("/") ? value : `${value}/`;
  }

  private initializeAuth(): void {
    const token = this.getToken();
    if (!token) return;

    if (!this.isTokenValid(token)) {
      console.log("[AUTH][API] Token inválido o expirado");
      this.clearAuth();
      return;
    }
    this.loadProfileForBackend(token);
  }

  private loadProfileForBackend(token: string): void {
    const userProfile = this.loadProfileFromLocalStorage();
    if (userProfile) {
      this.setAuthenticatedProfile(userProfile, false);
    } else {
      this.loadProfileFromToken(token);
    }

    queueMicrotask(() => {
      this.refreshAuthenticatedUser().subscribe({
        error: (error) => {
          console.error("[AUTH][API] Error al cargar /auth/me:", error);
          if (!this.getCurrentUserValue()) {
            this.clearAuth();
          }
        },
      });
    });
  }

  private isTokenValid(token: string): boolean {
    try {
      const decoded = jwtDecode<BackendJwtClaims>(token);
      if (decoded.exp) {
        const isExpired = decoded.exp * 1000 < Date.now();
        if (isExpired) return false;
      }
      return true;
    } catch {
      return false;
    }
  }

  private loadProfileFromToken(token: string): void {
    try {
      const decodedToken = jwtDecode<BackendJwtClaims>(token);
      const usuarioId = Number(decodedToken.uid ?? decodedToken.usuarioId);
      const roles = this.normalizeRoleCodes(
        decodedToken.roles ?? (decodedToken.rol ? [decodedToken.rol] : []),
      );

      if (!usuarioId || !decodedToken.sub || roles.length === 0) {
        this.clearAuth();
        return;
      }

      const permisos = decodedToken.permisos?.length
        ? this.mapBackendPermissionCodes(decodedToken.permisos)
        : this.loadPermisosFromLocalStorage();
      const profile: CurrentUser = {
        id: usuarioId,
        usuarioNombre: decodedToken.sub,
        rolNombre: roles[0],
        roles,
        permisos,
      };

      this.setAuthenticatedProfile(profile);
    } catch (error) {
      console.error("[AUTH][API] Error al cargar perfil:", error);
      this.clearAuth();
    }
  }

  login(credentials: {
    usuario: string;
    contrasena: string;
  }): Observable<LoginResponse> {
    return this.loginBackend(credentials);
  }

  refreshAuthenticatedUser(): Observable<CurrentUser> {
    return this.http.get<AuthMeResponseDto>(`${this.backendAuthUrl}/me`).pipe(
      map((response) => this.mapBackendProfile(response)),
      tap((profile) => this.setAuthenticatedProfile(profile)),
    );
  }

  /**
   * Determina la ruta inicial del sistema según permisos del usuario.
   * Regla: la primera vista debe ser la primera opción del panel lateral a la que el usuario tenga acceso.
   * - No depende de rutas fijas como /dashboard.
   */
  getDefaultLandingUrl(): string {
    const items = this.filtrarMenuPorPermisos(NavigationItems);
    const first = this.findFirstUrl(items);
    return first ?? "/acceso-denegado";
  }

  private filtrarMenuPorPermisos(items: NavigationItem[]): NavigationItem[] {
    // Admin: mostrar todo (respetando hidden explícito)
    if (this.hasRole("ADMINISTRADOR")) {
      return this.stripHidden(items);
    }

    const filtrarRec = (list: NavigationItem[]): NavigationItem[] => {
      return (list || [])
        .map((it) => {
          // Respeta hidden explícito en definición
          if (it.hidden) return { ...it, hidden: true };

          if (it.children && it.children.length > 0) {
            const children = filtrarRec(it.children);
            return { ...it, children, hidden: children.length === 0 };
          }

          if (!it.url) return it;

          const rule = getSidebarAccessRule(it.url);
          if (!rule?.modulo) {
            // Si no podemos mapearlo, por seguridad se oculta a no-admin
            return { ...it, hidden: true };
          }

          const permissionConfig = toPermissionConfig(rule);
          const visible = permissionConfig
            ? this.tienePermisoConEntidad(
                permissionConfig.modulo,
                permissionConfig.entidad,
                permissionConfig.acciones,
              )
            : this.tieneAccesoModulo(rule.modulo);

          return { ...it, hidden: !visible };
        })
        .filter((it) => !it.hidden);
    };

    return filtrarRec(items);
  }

  private stripHidden(items: NavigationItem[]): NavigationItem[] {
    const rec = (list: NavigationItem[]): NavigationItem[] =>
      (list || [])
        .filter((it) => !it.hidden)
        .map((it) =>
          it.children?.length ? { ...it, children: rec(it.children) } : it,
        )
        .filter((it) => !it.hidden);

    return rec(items);
  }

  private findFirstUrl(items: NavigationItem[]): string | null {
    for (const it of items || []) {
      if (it.type === "item" && it.url && !it.hidden) {
        return it.url;
      }
      if (it.children && it.children.length) {
        const found = this.findFirstUrl(it.children);
        if (found) return found;
      }
    }
    return null;
  }

  private loginBackend(credentials: {
    usuario: string;
    contrasena: string;
  }): Observable<LoginResponse> {
    const request: AuthLoginRequestDto = {
      usuario: credentials.usuario,
      password: credentials.contrasena,
    };

    return this.http
      .post<AuthResponseDto>(`${this.backendAuthUrl}/login`, request)
      .pipe(
        map((response) => this.mapBackendLoginResponse(response)),
        tap((response: LoginResponse) => {
          if (!response.token) throw new Error("Respuesta sin token");
          if (!this.isTokenValid(response.token))
            throw new Error("Token inválido recibido");

          this.setToken(response.token);

          const userProfile: CurrentUser = {
            id: response.usuarioId,
            usuarioNombre: response.usuario || "Desconocido",
            rolNombre: response.rol || "Sin rol",
            roles: response.roles || [response.rol].filter(Boolean),
            permisos: response.permisos || [],
          };

          this.setAuthenticatedProfile(userProfile);
        }),
      );
  }

  private mapBackendLoginResponse(response: AuthResponseDto): LoginResponse {
    if (!response?.token) {
      throw new Error("Respuesta sin token");
    }

    const decodedToken = jwtDecode<BackendJwtClaims>(response.token);
    const usuarioId = Number(
      response.usuario?.id ?? decodedToken.uid ?? decodedToken.usuarioId,
    );
    if (!usuarioId) {
      throw new Error("Respuesta sin usuario");
    }

    const roles = this.normalizeRoleCodes(
      response.roles?.map((rol) => rol.codigo || rol.nombre) ??
        decodedToken.roles ??
        [],
    );
    const permisos = this.mapBackendPermissionCodes(
      response.permisos ?? decodedToken.permisos ?? [],
    );

    return {
      token: response.token,
      usuario: response.usuario?.usuario || decodedToken.sub || "",
      rol: roles[0] || "SIN_ROL",
      roles,
      usuarioId,
      permisos,
    };
  }

  private mapBackendProfile(response: AuthMeResponseDto): CurrentUser {
    const roles = this.normalizeRoleCodes(
      response.roles?.map((rol) => rol.codigo || rol.nombre) ?? [],
    );
    return {
      id: Number(response.usuario?.id),
      usuarioNombre: response.usuario?.usuario || "",
      rolNombre: roles[0] || "SIN_ROL",
      roles,
      permisos: this.mapBackendPermissionCodes(response.permisos ?? []),
    };
  }

  private setAuthenticatedProfile(profile: CurrentUser, persist = true): void {
    const normalizedProfile: CurrentUser = {
      ...profile,
      roles: this.normalizeRoleCodes(
        profile.roles ?? (profile.rolNombre ? [profile.rolNombre] : []),
      ),
      permisos: this.normalizePermissions(profile.permisos || []),
    };

    if (persist) {
      this.saveProfileToLocalStorage(normalizedProfile);
      this.savePermisosToLocalStorage(normalizedProfile.permisos);
    }

    this.isAuthenticatedSubject.next(true);
    this.currentUserSubject.next(normalizedProfile);
    this.permisosSubject.next(normalizedProfile.permisos);
  }

  logout(): void {
    this.clearAuth();
    this.router.navigate(["/authentication/login"]);
  }

  clearAuth(): void {
    this.closeSessionOverlays();
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userProfileKey);
    localStorage.removeItem(this.PERMISOS_KEY);
    this.isAuthenticatedSubject.next(false);
    this.currentUserSubject.next(null);
    this.permisosSubject.next([]);
  }

  private closeSessionOverlays(): void {
    try {
      this.ngbModal.dismissAll("SESSION_ENDED");
    } catch {}
    this.alertService.cerrar();
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  private setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  isCurrentTokenValid(): boolean {
    const token = this.getToken();
    if (!token) return false;
    return this.isTokenValid(token);
  }

  isAuthenticated(): boolean {
    return this.getToken() !== null && this.isCurrentTokenValid();
  }

  private saveProfileToLocalStorage(profile: CurrentUser): void {
    localStorage.setItem(this.userProfileKey, JSON.stringify(profile));
  }

  private loadProfileFromLocalStorage(): CurrentUser | null {
    const profileJson = localStorage.getItem(this.userProfileKey);
    if (!profileJson) return null;

    try {
      const profile = JSON.parse(profileJson) as CurrentUser;
      return {
        ...profile,
        roles: this.normalizeRoleCodes(
          profile.roles ?? (profile.rolNombre ? [profile.rolNombre] : []),
        ),
        permisos: this.normalizePermissions(profile.permisos || []),
      };
    } catch {
      return null;
    }
  }

  getCurrentUserValue(): CurrentUser | null {
    return this.currentUserSubject.value;
  }

  getUsuario(): string | null {
    return this.getCurrentUserValue()?.usuarioNombre || null;
  }

  getUsuarioId(): number | null {
    return this.getCurrentUserValue()?.id || null;
  }

  getRol(): string | null {
    return this.getCurrentUserValue()?.rolNombre || null;
  }

  private savePermisosToLocalStorage(permisos: Permiso[]): void {
    localStorage.setItem(
      this.PERMISOS_KEY,
      JSON.stringify(this.normalizePermissions(permisos)),
    );
  }

  private loadPermisosFromLocalStorage(): Permiso[] {
    const permisosStr = localStorage.getItem(this.PERMISOS_KEY);
    return permisosStr
      ? this.normalizePermissions(JSON.parse(permisosStr))
      : [];
  }

  getPermisos(): Permiso[] {
    return this.permisosSubject.value;
  }

  tienePermiso(modulo: string, accion: string): boolean {
    if (this.hasRole("ADMINISTRADOR")) return true;
    const moduloNormalizado = this.normalizeModule(modulo);
    const accionNormalizada = this.normalizeAction(accion);
    const permisos = this.getPermisos();
    return permisos.some(
      (p) =>
        this.normalizeModule(p.modulo) === moduloNormalizado &&
        this.actionsMatch(p.accion, accionNormalizada),
    );
  }

  tieneAccesoModulo(modulo: string): boolean {
    if (this.hasRole("ADMINISTRADOR")) return true;
    const moduloNormalizado = this.normalizeModule(modulo);
    const permisos = this.getPermisos();
    return permisos.some(
      (p) => this.normalizeModule(p.modulo) === moduloNormalizado,
    );
  }

  tieneAlgunaAccion(modulo: string, acciones: string[]): boolean {
    const permisos = this.getPermisos();
    const moduloNormalizado = this.normalizeModule(modulo);
    return permisos.some(
      (p) =>
        this.normalizeModule(p.modulo) === moduloNormalizado &&
        acciones.some((a) => this.actionsMatch(p.accion, a)),
    );
  }

  tieneTodasLasAcciones(modulo: string, acciones: string[]): boolean {
    const permisos = this.getPermisos();
    const moduloNormalizado = this.normalizeModule(modulo);
    return acciones.every((accionItem) =>
      permisos.some(
        (p) =>
          this.normalizeModule(p.modulo) === moduloNormalizado &&
          this.actionsMatch(p.accion, accionItem),
      ),
    );
  }

  hasRole(role: string): boolean {
    const roleNormalizado = this.normalizeRole(role);
    const currentUser = this.getCurrentUserValue();
    const roles = this.normalizeRoleCodes(
      currentUser?.roles ??
        (currentUser?.rolNombre ? [currentUser.rolNombre] : []),
    );
    return roles.some((item) => item === roleNormalizado);
  }

  hasAnyRole(roles: string[]): boolean {
    const currentUser = this.getCurrentUserValue();
    const userRoles = this.normalizeRoleCodes(
      currentUser?.roles ??
        (currentUser?.rolNombre ? [currentUser.rolNombre] : []),
    );
    if (!userRoles.length) return false;
    const requiredRoles = this.normalizeRoleCodes(roles);
    return requiredRoles.some((role) => userRoles.includes(role));
  }

  getAuthInfo(): {
    isAuthenticated: boolean;
    user: CurrentUser | null;
    permisos: Permiso[];
    rol: string | null;
  } {
    return {
      isAuthenticated: this.isAuthenticated(),
      user: this.getCurrentUserValue(),
      permisos: this.getPermisos(),
      rol: this.getRol(),
    };
  }

  actualizarPermisos(permisos: Permiso[]): void {
    const normalized = this.normalizePermissions(permisos);
    this.savePermisosToLocalStorage(normalized);
    this.permisosSubject.next(normalized);

    const currentUser = this.getCurrentUserValue();
    if (currentUser) {
      currentUser.permisos = normalized;
      this.saveProfileToLocalStorage(currentUser);
      this.currentUserSubject.next(currentUser);
    }
  }

  tienePermisoConEntidad(
    modulo: string,
    entidad: string,
    acciones: string[],
  ): boolean {
    if (this.hasRole("ADMINISTRADOR")) return true;
    const moduloNormalizado = this.normalizeModule(modulo);
    const entidadNormalizada = this.normalizeEntity(entidad);
    const permisos = this.getPermisos();
    return acciones.some((accionRequerida) =>
      permisos.some(
        (permiso) =>
          this.normalizeModule(permiso.modulo) === moduloNormalizado &&
          this.entitiesMatch(permiso.entidad, entidadNormalizada) &&
          this.actionsMatch(permiso.accion, accionRequerida),
      ),
    );
  }

  tienePermisosMultiples(
    configs: Array<{ modulo: string; entidad: string; acciones: string[] }>,
  ): boolean {
    return configs.every((config) =>
      this.tienePermisoConEntidad(
        config.modulo,
        config.entidad,
        config.acciones,
      ),
    );
  }

  tieneAlgunPermisoDeMultiples(
    configs: Array<{ modulo: string; entidad: string; acciones: string[] }>,
  ): boolean {
    return configs.some((config) =>
      this.tienePermisoConEntidad(
        config.modulo,
        config.entidad,
        config.acciones,
      ),
    );
  }

  private mapBackendPermissionCodes(codes: string[]): Permiso[] {
    return (codes || [])
      .map((code) => this.mapBackendPermissionCode(code))
      .filter((permiso): permiso is Permiso => permiso !== null);
  }

  private mapBackendPermissionCode(code: string): Permiso | null {
    const [rawModulo, rawAccion] = String(code || "").split(":");
    if (!rawModulo || !rawAccion) return null;

    const modulo = this.normalizeModule(rawModulo);
    return {
      modulo,
      entidad: this.inferEntityFromModule(modulo),
      accion: this.normalizeAction(rawAccion),
    };
  }

  private normalizePermissions(permisos: Permiso[]): Permiso[] {
    return (permisos || []).map((permiso) => {
      const modulo = this.normalizeModule(permiso.modulo);
      return {
        ...permiso,
        modulo,
        entidad: this.normalizeEntity(
          permiso.entidad || this.inferEntityFromModule(modulo),
        ),
        accion: this.normalizeAction(permiso.accion),
      };
    });
  }

  private normalizeRoleCodes(roles: string[]): string[] {
    return Array.from(
      new Set(
        (roles || []).map((role) => this.normalizeRole(role)).filter(Boolean),
      ),
    );
  }

  private normalizeRole(role: string): string {
    return String(role || "")
      .trim()
      .replace(/\s+/g, "_")
      .replace(/-/g, "_")
      .toUpperCase();
  }

  private normalizeModule(modulo: string): string {
    const key = String(modulo || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/_/g, "-");

    const aliases: Record<string, string> = {
      "historia-clinica": "historial_medico",
      "historial-medico": "historial_medico",
      pagos: "caja",
    };

    return aliases[key] ?? key.replace(/-/g, "_");
  }

  private normalizeEntity(entidad: string): string {
    return String(entidad || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/-/g, "_");
  }

  private normalizeAction(accion: string): string {
    const key = String(accion || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/_/g, "-");

    const aliases: Record<string, string> = {
      ver: "LEER",
      listar: "LEER",
      leer: "LEER",
      crear: "CREAR",
      registrar: "CREAR",
      editar: "EDITAR",
      actualizar: "EDITAR",
      eliminar: "ELIMINAR",
      anular: "ANULAR",
      baja: "CAMBIAR_ESTADO",
      activar: "CAMBIAR_ESTADO",
      desactivar: "CAMBIAR_ESTADO",
      "cambiar-estado": "CAMBIAR_ESTADO",
      "ver-registros": "VER_REGISTROS",
      "registrar-pago": "REGISTRAR_PAGO",
      "imprimir-ticket": "IMPRIMIR_TICKET",
    };

    return aliases[key] ?? key.toUpperCase().replace(/-/g, "_");
  }

  private actionsMatch(actual: string, required: string): boolean {
    const actualNormalized = this.normalizeAction(actual);
    const requiredNormalized = this.normalizeAction(required);
    if (actualNormalized === requiredNormalized) return true;

    const groups = [
      ["LEER", "VER", "LISTAR", "VER_REGISTROS"],
      ["CREAR", "REGISTRAR", "REGISTRAR_PAGO"],
      ["EDITAR", "ACTUALIZAR"],
      ["CAMBIAR_ESTADO", "ACTIVAR", "DESACTIVAR", "BAJA", "ELIMINAR"],
    ];

    return groups.some(
      (group) =>
        group.includes(actualNormalized) && group.includes(requiredNormalized),
    );
  }

  private entitiesMatch(actual: string, required: string): boolean {
    const actualNormalized = this.normalizeEntity(actual);
    const requiredNormalized = this.normalizeEntity(required);
    return !actualNormalized || actualNormalized === requiredNormalized;
  }

  private inferEntityFromModule(modulo: string): string {
    const map: Record<string, string> = {
      usuarios: "usuario",
      roles: "rol",
      permisos: "permiso",
      pacientes: "paciente",
      citas: "cita",
      historial_medico: "historial",
      medicos: "medico",
      horarios: "horario",
      servicios: "servicio",
      caja: "caja",
      reportes: "reporte",
      cirugias: "cirugia",
      internaciones: "internacion",
      farmacia: "farmacia",
    };

    return map[this.normalizeModule(modulo)] ?? this.normalizeModule(modulo);
  }
}
